import React, { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { Schedule, PlayArrow } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  headerIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.3rem",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  cronPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.05)"
      : "rgba(0,0,0,0.03)",
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
  },
  cronExpression: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)",
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.spacing(0.5),
  },
  testButton: {
    marginLeft: "auto",
    color: theme.palette.success?.main || theme.palette.primary.main,
  },
  cronDescription: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    flex: 1,
  },
  scheduleIcon: {
    fontSize: "1rem",
    color: theme.palette.text.secondary,
  },
  weekdaysContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    paddingTop: theme.spacing(0.5),
  },
}));

const WEEKDAYS = [
  { value: "1", label: "Seg" },
  { value: "2", label: "Ter" },
  { value: "3", label: "Qua" },
  { value: "4", label: "Qui" },
  { value: "5", label: "Sex" },
  { value: "6", label: "Sáb" },
  { value: "0", label: "Dom" },
];

// --- Funções utilitárias (exportadas para reuso) ---

/**
 * Parseia uma expressão cron (5 campos) e retorna objeto com modo e valores
 */
export function parseCron(cron) {
  const defaults = { mode: "daily", minute: "0", hour: "4", interval: "1", weekdays: [] };
  if (!cron || cron.trim() === "") return defaults;

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return defaults;

  const [min, hr, , , dow] = parts;

  // A cada X minutos: */X * * * *
  const everyMinMatch = min.match(/^\*\/(\d+)$/);
  if (everyMinMatch && hr === "*") {
    return { mode: "everyMinutes", minute: "0", hour: "0", interval: everyMinMatch[1], weekdays: [] };
  }

  // A cada X horas: M */X * * *
  const everyHrMatch = hr.match(/^\*\/(\d+)$/);
  if (everyHrMatch && /^\d+$/.test(min)) {
    return { mode: "everyHours", minute: min, hour: "0", interval: everyHrMatch[1], weekdays: [] };
  }

  // Diário ou dias específicos: M H * * DOW
  if (/^\d+$/.test(min) && /^\d+$/.test(hr)) {
    if (dow !== "*") {
      return { mode: "weekdays", minute: min, hour: hr, interval: "1", weekdays: dow.split(",").filter(Boolean) };
    }
    return { mode: "daily", minute: min, hour: hr, interval: "1", weekdays: [] };
  }

  return defaults;
}

/**
 * Gera expressão cron a partir dos parâmetros
 */
export function buildCron(mode, hour, minute, interval, weekdays) {
  switch (mode) {
    case "everyMinutes":
      return `*/${interval} * * * *`;
    case "everyHours":
      return `${minute} */${interval} * * *`;
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekdays":
      if (weekdays.length === 0) return `${minute} ${hour} * * *`;
      return `${minute} ${hour} * * ${weekdays.join(",")}`;
    default:
      return `${minute} ${hour} * * *`;
  }
}

/**
 * Gera descrição legível de uma expressão cron
 */
export function describeCron(mode, hour, minute, interval, weekdays) {
  const pad = (n) => String(n).padStart(2, "0");

  switch (mode) {
    case "everyMinutes":
      if (interval === "1") return "A cada minuto";
      return `A cada ${interval} minutos`;
    case "everyHours":
      if (interval === "1") return `A cada hora, no minuto ${pad(minute)}`;
      return `A cada ${interval} horas, no minuto ${pad(minute)}`;
    case "daily":
      return `Diariamente às ${pad(hour)}:${pad(minute)}`;
    case "weekdays": {
      const dayNames = weekdays
        .map((d) => WEEKDAYS.find((w) => w.value === d)?.label)
        .filter(Boolean);
      if (dayNames.length === 0) return `Diariamente às ${pad(hour)}:${pad(minute)}`;
      return `${dayNames.join(", ")} às ${pad(hour)}:${pad(minute)}`;
    }
    default:
      return "";
  }
}

/**
 * Descreve uma expressão cron diretamente (sem precisar do estado do builder)
 */
export function describeCronExpression(cron) {
  const { mode, hour, minute, interval, weekdays } = parseCron(cron);
  return describeCron(mode, hour, minute, interval, weekdays);
}

// --- Componente ---

/**
 * CronBuilder - Componente reutilizável para criar expressões cron visualmente
 *
 * Props:
 *   value       - expressão cron atual (string, ex: "0 4 * * *")
 *   onChange     - callback(cronExpression: string) quando muda
 *   label       - título da caixa (default: "Agendamento")
 *   description - subtítulo opcional
 *   icon        - ícone customizado (default: Schedule)
 *   compact     - se true, não renderiza a caixa com borda (default: false)
 *   onTest      - callback para executar manualmente (se definido, mostra botão de teste)
 *   testRunning - se true, mostra spinner no botão de teste
 *   testLabel   - tooltip do botão de teste (default: "Executar agora")
 */
export default function CronBuilder({
  value,
  onChange,
  label = "Agendamento",
  description,
  icon: IconComponent,
  compact = false,
  onTest,
  testRunning = false,
  testLabel = "Executar agora",
}) {
  const classes = useStyles();

  const parsed = parseCron(value);
  const [mode, setMode] = useState(parsed.mode);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [interval, setInterval] = useState(parsed.interval);
  const [weekdays, setWeekdays] = useState(parsed.weekdays);

  useEffect(() => {
    const p = parseCron(value);
    setMode(p.mode);
    setHour(p.hour);
    setMinute(p.minute);
    setInterval(p.interval);
    setWeekdays(p.weekdays);
  }, [value]);

  function emitChange(newMode, newHour, newMinute, newInterval, newWeekdays) {
    const cron = buildCron(newMode, newHour, newMinute, newInterval, newWeekdays);
    if (onChange) onChange(cron);
  }

  function handleModeChange(newMode) {
    setMode(newMode);
    if (newMode === "everyMinutes") {
      setInterval("5");
      emitChange(newMode, hour, minute, "5", weekdays);
    } else if (newMode === "everyHours") {
      setInterval("1");
      setMinute("0");
      emitChange(newMode, hour, "0", "1", weekdays);
    } else {
      emitChange(newMode, hour, minute, interval, weekdays);
    }
  }

  function handleHourChange(v) {
    const val = Math.max(0, Math.min(23, parseInt(v, 10) || 0));
    setHour(String(val));
    emitChange(mode, String(val), minute, interval, weekdays);
  }

  function handleMinuteChange(v) {
    const val = Math.max(0, Math.min(59, parseInt(v, 10) || 0));
    setMinute(String(val));
    emitChange(mode, hour, String(val), interval, weekdays);
  }

  function handleIntervalChange(v) {
    const val = Math.max(1, parseInt(v, 10) || 1);
    setInterval(String(val));
    emitChange(mode, hour, minute, String(val), weekdays);
  }

  function handleWeekdayToggle(day) {
    const newDays = weekdays.includes(day)
      ? weekdays.filter((d) => d !== day)
      : [...weekdays, day];
    setWeekdays(newDays);
    emitChange(mode, hour, minute, interval, newDays);
  }

  const cronExpression = buildCron(mode, hour, minute, interval, weekdays);
  const cronDescription = describeCron(mode, hour, minute, interval, weekdays);

  const Icon = IconComponent || Schedule;

  const content = (
    <>
      {/* Header */}
      {!compact && (
        <div className={classes.header}>
          <Icon className={classes.headerIcon} />
          <div>
            <Typography className={classes.headerTitle}>{label}</Typography>
            {description && (
              <Typography variant="caption" color="textSecondary">
                {description}
              </Typography>
            )}
          </div>
        </div>
      )}

      <Grid container spacing={2} alignItems="center">
        {/* Modo */}
        <Grid item xs={12} sm={mode === "everyMinutes" ? 6 : 4}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>{compact ? label : "Frequência"}</InputLabel>
            <Select
              value={mode}
              onChange={(e) => handleModeChange(e.target.value)}
              label={compact ? label : "Frequência"}
            >
              <MenuItem value="everyMinutes">A cada X minutos</MenuItem>
              <MenuItem value="everyHours">A cada X horas</MenuItem>
              <MenuItem value="daily">Diariamente</MenuItem>
              <MenuItem value="weekdays">Dias da semana</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Intervalo (minutos ou horas) */}
        {(mode === "everyMinutes" || mode === "everyHours") && (
          <Grid item xs={6} sm={3}>
            <TextField
              label={mode === "everyMinutes" ? "Minutos" : "Horas"}
              variant="outlined"
              type="number"
              size="small"
              fullWidth
              value={interval}
              onChange={(e) => handleIntervalChange(e.target.value)}
              inputProps={{ min: 1, max: mode === "everyMinutes" ? 59 : 23 }}
            />
          </Grid>
        )}

        {/* Minuto (para everyHours) */}
        {mode === "everyHours" && (
          <Grid item xs={6} sm={3}>
            <TextField
              label="No minuto"
              variant="outlined"
              type="number"
              size="small"
              fullWidth
              value={minute}
              onChange={(e) => handleMinuteChange(e.target.value)}
              inputProps={{ min: 0, max: 59 }}
            />
          </Grid>
        )}

        {/* Hora e Minuto (para daily e weekdays) */}
        {(mode === "daily" || mode === "weekdays") && (
          <>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Hora (0-23)"
                variant="outlined"
                type="number"
                size="small"
                fullWidth
                value={hour}
                onChange={(e) => handleHourChange(e.target.value)}
                inputProps={{ min: 0, max: 23 }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Minuto (0-59)"
                variant="outlined"
                type="number"
                size="small"
                fullWidth
                value={minute}
                onChange={(e) => handleMinuteChange(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
              />
            </Grid>
          </>
        )}

        {/* Dias da semana */}
        {mode === "weekdays" && (
          <Grid item xs={12}>
            <div className={classes.weekdaysContainer}>
              {WEEKDAYS.map((day) => (
                <Chip
                  key={day.value}
                  label={day.label}
                  size="small"
                  color={weekdays.includes(day.value) ? "primary" : "default"}
                  onClick={() => handleWeekdayToggle(day.value)}
                  variant={weekdays.includes(day.value) ? "default" : "outlined"}
                  clickable
                />
              ))}
            </div>
          </Grid>
        )}
      </Grid>

      {/* Preview */}
      <div className={classes.cronPreview}>
        <Schedule className={classes.scheduleIcon} />
        <Typography className={classes.cronDescription}>
          {cronDescription}
        </Typography>
        <Typography className={classes.cronExpression}>
          {cronExpression}
        </Typography>
        {onTest && (
          <Tooltip title={testLabel}>
            <span>
              <IconButton
                size="small"
                className={classes.testButton}
                onClick={onTest}
                disabled={testRunning}
              >
                {testRunning ? <CircularProgress size={18} /> : <PlayArrow />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
    </>
  );

  if (compact) {
    return <div>{content}</div>;
  }

  return <div className={classes.container}>{content}</div>;
}
